import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
    Card,
    Grid,
    List,
    ListItemButton,
    Paper,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Typography,
    useTheme
} from '@mui/material';

import { LoadingScreen } from "../../../partials/LoadingScreen";
import StyledTableRow from '../../../styled/TableRow';
import StyledTableCell from '../../../styled/TableCell';

import apiClient from "../../../../services/api";
import store from "../../../../store";
import { toggleMessageOpen } from "../../../../store/actions/Message";

const CustomerList = ({ customers, t, handleCustomerClicked }) => {
    const theme = useTheme();

    return (
        <List sx={{height: '97%', overflow: 'auto'}}>
            {customers && customers.length > 0 ? (
                customers.map((item, index) => (
                    <ListItemButton
                        sx={{marginBottom: '10px', padding: 0}}
                        key={`user`+index}
                        component="div"
                        disablePadding
                        onClick={() => {handleCustomerClicked(item.customer_number)}}
                    >
                        <Card 
                            sx={{
                                width: '100%',
                                backgroundColor: theme.palette.background.tab,
                                border: localStorage.getItem('currentTheme') !== 'Dark'
                                    ? '1px solid ' + theme.palette.border.main
                                    : 'none'
                            }}
                        >
                            <Grid container alignItems='center' justifyItems='center' style={{height: '86px'}}>
                                <Grid item xs={9}>
                                    <div>{item.name}</div>
                                    <div style={{color: theme.palette.text.secondary}}>{item.customer_number}</div>
                                </Grid>
                            </Grid>
                        </Card>
                    </ListItemButton>
                ))
            ) : (
                <Typography>{t('No customers found!')}</Typography>
            )}
        </List>
    )
}

const ProjectList = ({t, projects, setProjects}) => {
    const [orderBy, setOrderBy] = useState(null);
    const [direction, setDirection] = useState('asc');

    useEffect(() => {
        const dir = direction === 'asc' ? 1 : -1;
        const clone = projects;

        clone.sort((a, b) => {
            const va = a[orderBy];
            const vb = b[orderBy];
            
            if(va === vb) {
                return 0;
            }

            if(typeof va === 'string') {
                return va.localeCompare(vb) * dir;
            }

            return (va > vb ? 1 : -1) * dir;
        });

        setProjects(clone);
    }, [orderBy, direction, projects]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSort = (id) => {
        if(id === orderBy) {
            if(direction === 'asc') {
                setDirection('desc')
            } else {
                setDirection('asc');
            }
        } else {
            setDirection('asc');
            setOrderBy(id);
        }
    }

    return (
        <TableContainer sx={{height: '97%', overflow: 'auto'}}>
            <Table stickyHeader aria-label="projects table" size='small'>
                <TableHead>
                    <TableRow>
                        <StyledTableCell>
                            <TableSortLabel
                                direction={direction}
                                active={orderBy === 'name'}
                                onClick={() => handleSort('name')}
                            >
                                {t('Name')}
                            </TableSortLabel>
                        </StyledTableCell>
                        <StyledTableCell>
                            <TableSortLabel
                                direction={direction}
                                active={orderBy === 'number'}
                                onClick={() => handleSort('number')}
                            >
                                {t('Number')}
                            </TableSortLabel>
                        </StyledTableCell>
                        <StyledTableCell style={{width: '5%'}}>
                            <TableSortLabel
                                direction={direction}
                                active={orderBy === 'views'}
                                onClick={() => handleSort('views')}
                            >
                                {t('Views')}
                            </TableSortLabel>
                        </StyledTableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {projects.map((project, index) => {
                        return (
                            <StyledTableRow key={index}>
                                <StyledTableCell>{project.name}</StyledTableCell>
                                <StyledTableCell>{project.number}</StyledTableCell>
                                <StyledTableCell style={{width: '5%'}}>{project.views}</StyledTableCell>
                            </StyledTableRow>
                        )
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

export default function ProjectViews() {
    const {t} = useTranslation();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [customers, setCustomers] = useState([]);
    const [projects, setProjects] = useState([]);

    useEffect(() => {
        setLoading(true);

        apiClient.post('/api/modules/check', {name: 'publicViews'}).then(response => {
            if(!response.data.success) {
                store.dispatch(toggleMessageOpen(t("You don't have permission to enter this page"), "error"))

                navigate('/app/home');
            } else {
                setLoading(false)
            }
        })
    }, [])// eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        apiClient.get('/api/customers/all').then(response => {
            if(response.data.success) {
                setCustomers(response.data.customers);
            }
        }).catch(error => {});
    }, [])

    useEffect(() => {
        if(! selectedCustomer) {
            return;
        }

        apiClient.post('/api/projects/get-by-customer', {'customerNumber': selectedCustomer}).then(response => {
            if(response.data.success) {
                setProjects(response.data.projects);
            }
        }).catch(error => {});
    }, [selectedCustomer])

    const handleCustomerClicked = (customerNumber) => {
        setSelectedCustomer(customerNumber)
    }

    const renderData = () => {
        return (
            <Grid container spacing={2} sx={{height: '100%', overflow: 'hidden'}}>
                <Grid item xs={6} sx={{height: '100%'}}>
                    <Paper sx={{padding: '10px', height: '100%'}}>
                        <Typography variant='h2' sx={{marginBottom: '5px'}}>
                            {t('Customers')}
                        </Typography>
                        <CustomerList
                            customers={customers}
                            handleCustomerClicked={handleCustomerClicked}
                            t={t}
                        />
                    </Paper>
                </Grid>
                <Grid item xs={6} sx={{height: '100%'}}>
                    <Paper sx={{padding: '10px', height: '100%'}}>
                        <Typography variant='h2' sx={{marginBottom: '5px'}}>
                            {t('Projects')}
                        </Typography>
                        <ProjectList
                            projects={projects}
                            setProjects={setProjects}
                            t={t}
                        />
                    </Paper>
                </Grid>
            </Grid>
        );
    }

    return (
        !loading ? renderData() : <LoadingScreen />
    );
}